import { Injectable } from '@angular/core'
import { Subject } from 'rxjs'

import { Favorite } from '../interfaces/resources/favorite.interface'
import { Incentive } from '../interfaces/resources/incentive.interface'
import { IncentiveFamily } from '../enums/incentive.family'

@Injectable({
  providedIn: 'root'
})
export class FavoriteService {
  favoriteIncentives = new Subject<Favorite[]>()
  localStorageKey = 'oecdRdTax'

  list(params?: { dataset?: IncentiveFamily }): Favorite[] {
    const appLocalStorage: {
      favoriteIncentives: Favorite[]
    } = JSON.parse(
      localStorage.getItem(this.localStorageKey) || '{}'
    )

    const favorites = appLocalStorage.favoriteIncentives || []

    if (params?.dataset) {
      return favorites.filter(
        favorite => favorite.family === params.dataset
      )
    }

    return favorites
  }

  toggle(
    selectedIncentive: Incentive,
    family: IncentiveFamily
  ): boolean {
    const favoriteIncentives = this.list()
    const isFavorite = this.isFavorite(selectedIncentive, family)

    if (isFavorite) {
      const index = favoriteIncentives.findIndex(
        favorite =>
          favorite.id === selectedIncentive.id &&
          favorite.family === family
      )

      if (index !== -1) {
        favoriteIncentives.splice(index, 1)
      }
    } else {
      favoriteIncentives.push({
        id: selectedIncentive.id,
        label: selectedIncentive.label,
        image: selectedIncentive.country.image,
        family
      })
    }

    this.save(favoriteIncentives)

    return !isFavorite
  }

  add(
    incentives: Incentive | Incentive[],
    family: IncentiveFamily
  ): void {
    if (!Array.isArray(incentives)) {
      incentives = [incentives]
    }

    const favoriteIncentives = this.list()

    incentives.forEach(incentive => {
      if (!this.isFavorite(incentive, family)) {
        favoriteIncentives.push({
          id: incentive.id,
          label: incentive.label,
          image: incentive.country.image,
          family
        })
      }
    })

    this.save(favoriteIncentives)
  }

  isFavorite(
    incentive: Incentive | string,
    family?: IncentiveFamily
  ): boolean {
    const id = typeof incentive === 'string'
      ? incentive
      : incentive.id

    return this.list().some(favorite =>
      favorite.id === id &&
      (!family || favorite.family === family)
    )
  }

  remove(
    favorites:
      | { id: string; family: IncentiveFamily }
      | { id: string; family: IncentiveFamily }[]
  ): void {
    const favoriteItems = Array.isArray(favorites)
      ? favorites
      : [favorites]

    const favoriteIncentives = this.list().filter(
      (favorite) =>
        !favoriteItems.some(
          (item) =>
            item.id === favorite.id &&
            item.family === favorite.family
        )
    )

    this.save(favoriteIncentives)
  }

  removeAllFavorites(): void {
    this.save([])
  }

  replaceByNewValues(
    incentives: Incentive[],
    family: IncentiveFamily
  ): void {
    this.save(
      incentives.map(incentive => ({
        id: incentive.id,
        label: incentive.label,
        image: incentive.country.image,
        family
      }))
    )
  }

  private save(favoriteIncentives: Favorite[]): void {
    localStorage.setItem(
      this.localStorageKey,
      JSON.stringify({ favoriteIncentives })
    )

    this.favoriteIncentives.next(favoriteIncentives)
  }
}
